/**
 * Routing Event Builder
 * 
 * Utility to construct RoutingEvent objects from RoutingResult.
 * Ensures consistent event structure across all routing paths.
 */

import type { RoutingResult } from '../routingOrchestrator';
import type {
  RoutingEvent,
  RoutingEventSource,
  RoutingEventOutcome,
} from './types';

// Orchestrator version for traceability
export const ORCHESTRATOR_VERSION = '1.0.0';

/**
 * Build a RoutingEvent from a RoutingResult
 * 
 * This is the ONLY way to create routing events.
 * Ensures consistent structure and required fields.
 */
export function buildRoutingEvent(
  result: RoutingResult,
  source: RoutingEventSource,
  options: {
    simulationRunId?: string;
    virtualTime?: number;
  } = {}
): RoutingEvent {
  const { simulationRunId, virtualTime } = options;
  
  // Use virtual time for simulations, real time for live
  const timestamp = virtualTime ?? result.observability.completedAt;
  
  // Map outcome
  const outcome: RoutingEventOutcome = result.outcome;
  
  // Build event
  const event: RoutingEvent = {
    id: generateEventId(result.lead.id, timestamp),
    timestamp,
    source,
    
    lead: {
      id: result.lead.id,
      type: result.lead.leadTypeId,
      typeName: result.lead.leadTypeName,
      state: result.lead.state,
      vertical: result.lead.vertical,
      estimatedValue: result.lead.estimatedValue,
    },
    
    outcome,
    
    // Winner (only if routed)
    winner: result.deliveryImpact.agentId ? {
      agentId: result.deliveryImpact.agentId,
      agentName: result.deliveryImpact.agentName || 'Unknown',
      chargedAmount: result.deliveryImpact.chargedAmount,
    } : undefined,
    
    // Pricing
    pricing: {
      chargedAmount: result.deliveryImpact.chargedAmount,
      originalPrice: result.lead.originalPrice,
      floorPrice: result.lead.pricingSnapshot?.adminMinPrice,
    },
    
    // Scores
    scores: result.observability.allocationBreakdown ? {
      bidScore: result.observability.allocationBreakdown.bidScore,
      qualityScore: result.observability.allocationBreakdown.qualityScore,
      finalScore: result.observability.allocationBreakdown.finalScore,
      priorityScore: result.observability.priorityScore,
    } : undefined,
    
    // Guardrails
    guardrails: {
      applied: result.observability.guardrailsApplied,
      pacingCap: result.observability.guardrailSummary?.pacingCap !== undefined,
      poolBoost: result.observability.guardrailSummary?.poolBoost !== undefined,
      retryPriority: result.observability.guardrailSummary?.retryMultiplier !== undefined,
      pressureBoost: result.observability.pressureBoostApplied,
      pressureBoostValue: result.observability.pressureBoostValue,
    },
    
    // Pool metadata
    poolMeta: {
      strategy: result.observability.poolMeta?.strategy,
      poolSize: result.observability.poolSize,
      totalAgentsEvaluated: result.observability.totalAgentsEvaluated,
      eligibleCount: result.observability.eligibleCount,
      ineligibleCount: result.observability.ineligibleCount,
      pacingBoostApplied: result.observability.poolMeta?.pacingBoostApplied,
      contractPressureApplied: result.observability.poolMeta?.contractPressure?.contractPressureApplied,
    },
    
    // Retry information
    retry: result.observability.retryDecision ? {
      retryCount: result.observability.retryDecision.retryCount,
      maxRetries: result.observability.retryDecision.maxRetries,
      willRetry: result.observability.retryDecision.willRetry,
      reason: result.observability.retryDecision.reason,
    } : undefined,
    
    // Rejection info
    rejectionReason: result.observability.rejectionReason,
    rejectionCode: result.observability.rejectionCode,
    
    // Metadata
    metadata: {
      orchestratorVersion: ORCHESTRATOR_VERSION,
      simulationRunId,
      durationMs: result.observability.durationMs,
      startedAt: result.observability.startedAt,
      completedAt: result.observability.completedAt,
    },
  };
  
  return event;
}

/**
 * Generate a unique event ID
 */
function generateEventId(leadId: string, timestamp: number): string {
  const random = Math.random().toString(36).substring(2, 8);
  return `evt_${leadId}_${timestamp}_${random}`;
}
